import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignalRContext } from '../contexts/SignalRContext.tsx';
import { parseSignalRError } from '../utils/helpers.ts';
import Modal from '../components/common/Modal.tsx';
import LetterBackground from '../components/common/BubbleBackground.tsx';

// Google Analytics için global tip tanımı
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

const HomePage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [roomId, setRoomId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isHowToPlayOpen, setIsHowToPlayOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const navigate = useNavigate();
  const { connection, isConnected } = useSignalRContext();
  const cardRef = useRef<HTMLDivElement>(null!);

  // Cookie consent kontrolü
  useEffect(() => {
    const consent = localStorage.getItem('sos-analytics-consent');
    if (!consent) {
      setShowCookieConsent(true);
    }
  }, []);

  const handleAnalyticsConsent = (accepted: boolean) => {
    localStorage.setItem('sos-analytics-consent', accepted ? 'accepted' : 'declined');
    setShowCookieConsent(false);

    if (accepted && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted',
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted'
      });
    }
  };

  const handleCreateRoom = async () => {
    if (!connection || !username) return;
    setError(null);
    try {
      const newRoomId = await connection.invoke('CreateRoom', username);
      localStorage.setItem('sos-username', username);
      navigate(`/room/${newRoomId}`, { state: { username, isCreator: true } });
    } catch (err: any) {
      setError(parseSignalRError(err));
    }
  };

  const handleJoinRoom = async () => {
    if (!connection || !roomId || !username) return;
    setError(null);
    try {
      const formattedRoomId = roomId.trim().toUpperCase();
      await connection.invoke('JoinRoom', formattedRoomId, username);
      localStorage.setItem('sos-username', username);
      navigate(`/room/${formattedRoomId}`, { state: { username } });
    } catch (err: any) {
      setError(parseSignalRError(err));
    }
  };

  return (
    <>
      <LetterBackground cardRef={cardRef} />
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-100 to-purple-200 relative z-[1]">
        <div ref={cardRef} className="bg-white shadow-2xl rounded-3xl p-10 w-full max-w-lg flex flex-col items-center animate-fade-in relative z-[200]">
          <div className="flex flex-col items-center mb-8">
            <img src="/sosGame.png" alt="SOS Game Logo" className="w-24 h-24 mb-4 drop-shadow-lg rounded-xl bg-white shadow" />
            <h1 className="text-4xl font-extrabold text-gray-800 mb-1 tracking-tight">SOS Game</h1>
            <p className="text-gray-500 text-lg">Play with your friend in real time!</p>
          </div>
          <div className="w-full mb-6">
            <label className="block text-gray-700 font-semibold mb-1" htmlFor="username">Your Name</label>
            <input
              id="username"
              className="w-full px-4 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:outline-none text-base mb-2 shadow-sm"
              type="text"
              placeholder="Enter your name"
              value={username}
              onChange={e => setUsername(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="flex flex-col md:flex-row w-full gap-4 items-stretch justify-center">
            {/* Create Room */}
            <div className="flex-1 bg-blue-50 rounded-2xl p-5 flex flex-col justify-between items-center shadow-lg transition-transform duration-200 hover:scale-105 hover:shadow-2xl mb-4 md:mb-0 min-w-[200px]">
              <div className="w-full flex flex-col items-center">
                <div className="text-lg font-bold text-blue-700 mb-3">Create Room</div>
                <span className="text-gray-400 font-bold text-base md:text-lg select-none mb-2">Let's Play!</span>
              </div>
              <button
                className="w-11/12 mx-auto text-base font-bold py-2 rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg hover:from-blue-600 hover:to-purple-600 transition disabled:opacity-50 mt-6"
                onClick={handleCreateRoom}
                disabled={!username || !isConnected}
              >
                {isConnected ? 'Create Room' : 'Connecting...'}
              </button>
            </div>
            {/* Join Room */}
            <div className="flex-1 bg-green-50 rounded-2xl p-5 flex flex-col justify-between items-center shadow-lg transition-transform duration-200 hover:scale-105 hover:shadow-2xl min-w-[200px]">
              <div className="w-full flex flex-col items-center">
                <div className="text-lg font-bold text-green-700 mb-3">Join a Room</div>
                <input
                  id="roomId"
                  className="w-11/12 px-4 py-2 rounded-xl border border-gray-300 focus:ring-2 focus:ring-green-400 focus:outline-none text-base mb-3 tracking-widest uppercase shadow-sm"
                  type="text"
                  placeholder="Enter Room ID"
                  value={roomId}
                  onChange={e => setRoomId(e.target.value.toUpperCase())}
                  autoComplete="off"
                  disabled={!username}
                  maxLength={6}
                />
              </div>
              <button
                className="w-11/12 mx-auto text-base font-bold py-2 rounded-xl bg-gradient-to-r from-green-500 to-teal-400 text-white shadow-lg hover:from-green-600 hover:to-teal-500 transition disabled:opacity-50 mt-6"
                onClick={handleJoinRoom}
                disabled={!username || !roomId || !isConnected}
              >
                {isConnected ? 'Join Room' : 'Connecting...'}
              </button>
            </div>
          </div>
          {error && (
            <div className="mt-4 flex items-center justify-center">
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 shadow animate-shake" role="alert">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
                </svg>
                <span className="font-bold">Connection Error:</span>
                <span>{error} Please check your internet connection and server status.</span>
              </div>
            </div>
          )}
          {!isConnected && !error && (
            <div className="mt-6 flex flex-col items-center justify-center">
              <div className="flex items-center gap-2 animate-pulse">
                <svg className="w-6 h-6 text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                </svg>
                <span className="text-blue-600 font-semibold text-lg">Connecting to the game server, please wait...</span>
              </div>
            </div>
          )}

          <div className="mt-8 border-t pt-4 w-full flex justify-center">
            <button
              onClick={() => setIsHowToPlayOpen(true)}
              className="text-blue-600 hover:text-blue-800 font-semibold transition"
            >
              How to Play?
            </button>
          </div>
          <Modal
            isOpen={isHowToPlayOpen}
            onClose={() => setIsHowToPlayOpen(false)}
            title="How to Play SOS"
          >
            <div className="space-y-4 text-gray-700">
              <p>SOS is a classic two-player game, now available for up to 4 players!</p>
              <ul className="list-disc list-inside space-y-2">
                <li>Players take turns adding either an "S" or an "O" to any empty cell on the 8x8 grid.</li>
                <li>If a player forms an "SOS" sequence (horizontally, vertically, or diagonally), they score a point and get to play another turn immediately.</li>
                <li>Multiple SOSs can be formed in a single move, earning multiple points and extra turns.</li>
                <li>The game ends when the entire grid is filled.</li>
                <li>The player with the most points at the end of the game wins!</li>
              </ul>
              <p>Good luck and have fun!</p>
            </div>
          </Modal>

          <div className="mt-4 text-xs text-gray-400">Made with <span className="text-pink-500">♥</span> for your special game night!</div>

          {/* Footer: Privacy Policy & Contact */}
          <div className="mt-6 w-full flex flex-col items-center gap-2 text-xs text-gray-400">
            <button
              onClick={() => setIsPrivacyOpen(true)}
              className="underline hover:text-blue-600 transition"
              type="button"
            >
              Privacy Policy
            </button>
            <a
              href="mailto:info@playsos.online"
              className="underline hover:text-blue-600 transition flex items-center gap-1"
            >
              {/* Mail Icon */}
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline-block text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              info@playsos.online
            </a>
            <a
              href="https://buymeacoffee.com/metinhan"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-amber-600 transition flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline-block text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Support me
            </a>
          </div>

          {/* Cookie Consent Bottom Bar */}
          {showCookieConsent && (
            <div className="fixed bottom-0 left-0 right-0 z-50 bg-gray-900 text-white shadow-2xl border-t border-gray-700 animate-slide-up">
              <div className="max-w-6xl mx-auto px-4 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1">
                  <span className="text-2xl">🍪</span>
                  <p className="text-sm text-gray-300">
                    We use cookies to improve your experience.
                    <button
                      onClick={() => setIsPrivacyOpen(true)}
                      className="text-blue-400 hover:text-blue-300 underline ml-1"
                    >
                      Learn more
                    </button>
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleAnalyticsConsent(true)}
                    className="bg-gradient-to-r from-blue-500 to-purple-500 text-white py-2 px-6 rounded-lg font-bold hover:from-blue-600 hover:to-purple-600 transition shadow-lg whitespace-nowrap"
                  >
                    ✓ Accept All
                  </button>
                  <button
                    onClick={() => handleAnalyticsConsent(false)}
                    className="text-gray-500 text-xs hover:text-gray-400 transition"
                  >
                    Decline
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Privacy Policy Modal */}
          <Modal
            isOpen={isPrivacyOpen}
            onClose={() => setIsPrivacyOpen(false)}
            title="Privacy Policy"
          >
            <div className="space-y-3 text-gray-700 text-sm max-h-96 overflow-y-auto">
              <p>
                We value your privacy. This game does not collect, store, or share any personal data. Usernames are only used for in-game display and are not linked to any identity.
              </p>
              <p>
                <strong>Analytics:</strong> We use Google Analytics to understand how our game is used and improve your experience. This helps us see which features are popular and identify any issues. Google Analytics collects anonymous usage data such as page views, time spent, and device information. No personal information is collected or shared.
              </p>
              <p>
                <strong>Advertising:</strong> We use Google AdSense to display ads. If you accept cookies, you may see personalized ads based on your interests. If you decline, you will still see ads, but they will not be personalized.
              </p>
              <p>
                <strong>Cookies:</strong> We use cookies to remember your preferences for analytics and advertising. You can change this preference anytime by clearing your browser data.
              </p>
              <p>
                By playing, you agree to keep your username appropriate and respect other players. For any questions, contact us at <a href="mailto:info@playsos.online" className="underline text-blue-600">info@playsos.online</a>.
              </p>
              <p>
                This policy may be updated as needed. Please check back for changes.
              </p>
            </div>
          </Modal>
        </div>
      </div>
    </>
  );
};

export default HomePage; 